import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode, Constructor } from '@dojo/framework/core/interfaces';
import { Store } from '@dojo/framework/stores/Store';

import { Button } from '@dojo/widgets/button';
import { formatAriaProperties } from '@dojo/widgets/common/util';

import { createNlsMiddleware } from 'lcc-state';

import { QuizRPC, QuestionQueueType, QuizMeta } from '../interfaces';
import { QuestionQueue } from '../support/questionqueue';
import { quizstate } from '../support/state';
import { createProgressionMiddleware } from '../middleware/progression';
import { svc as flashcardsvc } from '../support/fc-svc';

import css from '../css/flashcard.m.css';

interface FlashcardIcache {
	inited: boolean;
}

export interface FlashcardProperties {
	quizstore: Store<QuizMeta>;
}

const nls = createNlsMiddleware('quiz');
const progression = createProgressionMiddleware();
const icache = createICacheMiddleware<FlashcardIcache>();
const factory = create({ icache, nls, theme, progression, quizstate }).properties<FlashcardProperties>();

export const Flashcard = factory(({ properties, middleware: { icache, nls, theme, progression, quizstate } }) => {
	icache.getOrSet('inited', () => {
		const { quizstore } = properties();
		quizstate.setStore(quizstore);
		progression.setStore(quizstore);
		progression.initialize();
		progression.svc = flashcardsvc;
		return true;
	});

	const buttons = (): DNode[] => {
		const {
			messages: { continue: continueText = '', start_over: restartText = '', begin: beginText = '' }
		} = nls();
		const { quiz = {} as QuizRPC } = progression.getProperties();
		const { use_restart } = quiz;
		const { svc } = progression;
		const themeCss = theme.classes(css);

		if (use_restart) {
			return [
				v('div', { xqa: 'action continue', classes: [themeCss.oneaction] }, [
					w(
						Button,
						{
							key: 'continue',
							onClick: async () => {
								const v = await svc.continue_quiz();
								progression.startquiz = v;
								progression.quiz_startup();
							}
						},
						[continueText]
					)
				]),
				v('div', { xqa: 'action restart', classes: [themeCss.oneaction] }, [
					w(
						Button,
						{
							key: 'restart',
							onClick: async () => {
								const v = await svc.restart_quiz();
								progression.startquiz = v;
								progression.quiz_startup(true);
							}
						},
						[restartText]
					)
				])
			];
		} else {
			return [
				v('div', { xqa: 'action begin', classes: [themeCss.oneaction] }, [
					w(
						Button,
						{
							key: 'begin',
							onClick: async () => {
								const v = await svc.start_quiz();
								progression.startquiz = v;
								progression.quiz_startup();
							}
						},
						[beginText]
					)
				])
			];
		}
	};

	const render_page = (): DNode[] => {
		const { isPlaceholder } = nls();
		if (isPlaceholder) {
			return [];
		}

		const themeCss = theme.classes(css);
		const { quiz = {} as QuizRPC, isrunning } = progression.getProperties();
		if (isrunning === 'quiz') {
			const { finalize, startquiz, svc } = progression;
			const quizstore = quizstate.getStore();
			return [w<QuestionQueueType>(QuestionQueue, { svc, finalize, startquiz, quizstore })];
		} else if (isrunning === 'final') {
			const { instructions_done } = quiz;
			return [
				v('div', {
					key: 'instructions',
					classes: [themeCss.instructions],
					innerHTML: instructions_done
				})
			];
		} else {
			const { instructions } = quiz;
			return [
				v('div', {
					key: 'instructions',
					id: 'quiz-instructions',
					classes: [themeCss.instructions],
					innerHTML: instructions
				}),
				v(
					'div',
					{
						key: 'quiz-actions',
						classes: [themeCss.actions],
						role: 'group',
						...formatAriaProperties({ labelledby: 'quiz-instructions' })
					},
					buttons()
				)
			];
		}
	};

	const { quiz, info } = progression.getProperties();
	const { load_quiz, render_loading } = progression;
	const hasdata = quiz && info;
	!hasdata && load_quiz();

	return v('div', { xqa: `progression flashcard` }, hasdata ? render_page() : render_loading());
});

export default Flashcard;
